import { useEffect, useState, useMemo } from "react";
import * as Yup from "yup";
import clsx from "clsx";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import GoogleAuth from "./googleAuth";
import { authenticationSuccess } from "../../../store/slice/utils";
import { FormFieldError } from "../../../components/atoms/FormFiledError";
import { setNotification } from "../../../store/slice/miscSlice";
import { useForgotPasswordMutation, useLoginMutation } from "../../../store/apiSlice/authApiSlice";
import { useNotification } from "../../../hooks/useNotification";
import { addEmailInResetPassword } from "../../../store/slice/authSlice";

// --- Constants & Helpers ---

/**
 * Returns a dynamic validation schema based on the current login step.
 *
 * @param {boolean} isPasswordRequired - If true, enforces password presence (Step 2).
 *                                       If false, only validates email (Step 1).
 * @returns {import("yup").ObjectSchema} The Yup validation schema.
 */
const getLoginSchema = isPasswordRequired =>
  Yup.object().shape({
    email: Yup.string().email("Wrong email format").required("Email is required"),
    password: Yup.string().when([], (_, schema) =>
      isPasswordRequired
        ? schema.required("Password is required")
        : schema.nullable().notRequired(),
    ),
  });

const usePasswordToggle = () => {
  const [visible, setVisible] = useState(false);
  const toggle = () => setVisible(prev => !prev);
  return { type: visible ? "text" : "password", visible, toggle };
};

/**
 * Resolve a user-facing login error message from an RTK Query error payload.
 *
 * @param {{status: (number|string), data?: {detail?: string}}} apiError
 * @return {string|null} Notification message for rate limits, or null.
 */
const getLoginRateLimitMessage = apiError => {
  if (apiError.status !== 429) {
    return null;
  }

  return (
    apiError?.data?.detail ||
    "Too many login attempts. Please wait before trying again."
  );
};

/**
 * The Login component for user authentication.
 *
 * Implements a 2-step authentication flow:
 * - Step 1: User enters email. System checks if account exists/requires SSO.
 * - Step 2: If traditional auth, user enters password.
 *
 * @component
 */
export function Login() {
  const dispatch = useDispatch();
  const notify = useNotification();
  const navigate = useNavigate();

  const [login, { data, error, isError, isLoading, isSuccess }] = useLoginMutation();
  const [forgotPassword] = useForgotPasswordMutation();

  const [isPasswordRequired, setIsPasswordRequired] = useState(false);
  const passwordToggle = usePasswordToggle();

  // Memoize validation schema so it doesn't get recreated on every render
  const validationSchema = useMemo(() => getLoginSchema(isPasswordRequired), [isPasswordRequired]);

  // Formik initialization
  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema,
    onSubmit: async values => {
      const payload = isPasswordRequired ? values : { email: values.email };
      await login(payload);
    },
  });

  // Handle successful login
  useEffect(() => {
    if (isSuccess) {
      if (data?.code === "VALID_USER") {
        setIsPasswordRequired(true);
      } else {
        dispatch(authenticationSuccess(data));
      }
    }
  }, [dispatch, data, isSuccess]);

  // Handle login errors
  useEffect(() => {
    if (isError) {
      if (error.status === "FETCH_ERROR") {
        dispatch(
          setNotification({
            type: "error",
            message: "Server Connection Failed. Please retry after a short while.",
          }),
        );
      } else {
        const rateLimitMessage = getLoginRateLimitMessage(error);

        if (rateLimitMessage) {
          dispatch(
            setNotification({
              type: "error",
              message: rateLimitMessage,
            }),
          );
        } else {
          formik.setErrors(error?.data);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isError]);

  // --- Field Handlers ---

  /**
   * Resets the form back to Step 1 (email only).
   * Keeps the previously entered email but clears the password and its touched state.
   */
  const changeEmail = () => {
    setIsPasswordRequired(false);
    // Keep the email, just clear the password
    formik.setFieldValue("password", "", false);
    formik.setFieldTouched("password", false, false);
  };

  /**
   * Triggers the Forgot Password flow.
   * Calls the API to send an OTP to the currently entered email.
   * On success, prepopulates Redux state and navigates to the Change Password screen.
   * Handles 429 rate-limiting specific to the OTP endpoint.
   */
  const handleForgotPass = async event => {
    event.preventDefault();
    try {
      const response = await forgotPassword({ email: formik.values.email }).unwrap();
      notify({ type: "success", message: response.detail });
      dispatch(addEmailInResetPassword(formik.values.email));
      navigate("/change_password/");
    } catch (apiError) {
      const detail = apiError.data?.detail;
      if (apiError.status === 429) {
        notify({
          type: "error",
          message: detail || "Please wait before requesting another security code.",
        });
      } else {
        notify({ type: "error", message: detail || "Something went wrong. Please try again." });
      }
    }
  };

  return (
    <div className="w-75 w-lg-50 py-10">
      <div className="text-center mb-12">
        <h1 className="fw-bolder">Sign In</h1>
      </div>

      <div className="row mb-12">
        <div className="col-8 offset-2">
          <GoogleAuth />
        </div>
      </div>

      <div className="separator separator-content mt-15 mb-12">
        <span className="w-125px text-gray-500 fw-semibold fs-7">Or with email</span>
      </div>

      <form id="orderlink_login_form" autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
        <div className="fv-row mb-8">
          <label
            className="form-label fs-6 fw-bolder text-dark"
            htmlFor="orderlink_login_email_input"
          >
            Email
          </label>

          {isPasswordRequired ? (
            <div className="d-flex justify-content-between form-control bg-transparent is-valid">
              <span>{formik.values.email}</span>
              <span className="cursor-pointer text-primary" onClick={changeEmail}>
                Change
              </span>
            </div>
          ) : (
            <>
              <input
                {...formik.getFieldProps("email")}
                id="orderlink_login_email_input"
                className={clsx(
                  "form-control bg-transparent",
                  { "is-invalid": formik.touched.email && formik.errors.email },
                  { "is-valid": formik.touched.email && !formik.errors.email },
                )}
                autoComplete="off"
                name="email"
                placeholder="Email"
                type="email"
              />
              <FormFieldError message={formik.touched.email && formik.errors.email} />
            </>
          )}
        </div>

        {isPasswordRequired && (
          <div className="fv-row mb-8 fv-plugins-icon-container" data-kt-password-meter="true">
            <div className="d-flex justify-content-between">
              <label
                className="form-label fw-bolder text-dark fs-6"
                htmlFor="orderlink_login_password_input"
              >
                Password
              </label>
              <span className="text-primary-color cursor-pointer h6" onClick={handleForgotPass}>
                Forgot Password
              </span>
            </div>

            <div className="position-relative">
              <input
                {...formik.getFieldProps("password")}
                id="orderlink_login_password_input"
                className={clsx("form-control bg-transparent pe-15", {
                  "is-invalid": formik.touched.password && formik.errors.password,
                })}
                autoComplete="off"
                name="password"
                placeholder="Password"
                type={passwordToggle.type}
              />
              <span
                className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-0"
                onClick={passwordToggle.toggle}
              >
                <i
                  className={clsx("ki-outline fs-2", {
                    "ki-eye-slash": !passwordToggle.visible,
                    "ki-eye": passwordToggle.visible,
                  })}
                />
              </span>
            </div>
            <FormFieldError message={formik.touched.password && formik.errors.password} />
          </div>
        )}

        <SubmitButtonDiv className="d-grid mb-10">
          <button
            id="orderlink_login_submit"
            disabled={
              isLoading ||
              !formik.isValid ||
              (isPasswordRequired ? !formik.values.password : !formik.values.email)
            }
            form="orderlink_login_form"
            className="btn"
            type="submit"
          >
            {isLoading ? (
              <span className="d-block indicator-progress">
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            ) : (
              <span className="indicator-label">Continue</span>
            )}
          </button>
        </SubmitButtonDiv>
      </form>
    </div>
  );
}

const SubmitButtonDiv = styled.div`
  button {
    background-color: #346dea;
    color: var(--character-primary-inverse, #fff);
    &:hover {
      background-color: #1249bf;
      color: var(--CLOUD, #fcfdfe);
    }
    &:disabled {
      background-color: #346dea;
      color: var(--character-primary-inverse, #fff);
      opacity: 0.75;
    }
  }
`;
